import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChatCircleDots, Compass, UserGear, Target, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";
import Disclaimer from "../components/Disclaimer";

const RECENT_KEY = "ccfl_recent_walkthrough";

const CHOICES = [
  {
    to: "/walkthrough?mode=guided",
    label: "Guide Me Through a Difficult Conversation",
    icon: Compass,
    testid: "home-cta-guide",
  },
  {
    to: "/coach",
    label: "Work with a Coach",
    icon: UserGear,
    testid: "home-cta-coach",
  },
];

export default function Home() {
  const nav = useNavigate();
  const [recent, setRecent] = useState(null);

  // Detect a recent in-progress walkthrough so we can offer "Continue"
  useEffect(() => {
    let alive = true;
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return;
    let saved;
    try {
      saved = JSON.parse(raw);
    } catch {
      localStorage.removeItem(RECENT_KEY);
      return;
    }
    if (!saved?.id) return;
    api
      .get(`/walkthrough/${saved.id}`)
      .then(({ data }) => {
        if (!alive) return;
        if (data?.id) {
          const preview = (data.description || "").slice(0, 120);
          setRecent({ id: data.id, preview });
        }
      })
      .catch(() => {
        localStorage.removeItem(RECENT_KEY);
      });
    return () => {
      alive = false;
    };
  }, []);

  const dismissRecent = () => {
    localStorage.removeItem(RECENT_KEY);
    setRecent(null);
  };

  return (
    <div>
      {/* Hero */}
      <section className="hero-bg border-b border-[#E8E4DB]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 lg:py-32">
          {recent && (
            <div
              className="bg-[#F3F0EA] border border-[#1E3A2B]/15 rounded-2xl p-5 mb-10 max-w-4xl anim-in delay-1"
              data-testid="home-resume-banner"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="label-kicker">Active engagement</div>
                  <p className="text-[#1E3A2B] mt-2 leading-relaxed">
                    You have a session in progress — <span className="italic text-[#4A524C]">"{recent.preview}"</span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={dismissRecent}
                    data-testid="home-resume-dismiss"
                    className="text-sm text-[#8A948E] hover:text-[#1E3A2B] px-3 py-2"
                  >
                    Start fresh
                  </button>
                  <button
                    onClick={() => nav(`/walkthrough/${recent.id}`)}
                    data-testid="home-resume-continue"
                    className="inline-flex items-center gap-1.5 bg-[#1E3A2B] text-white px-4 py-2 rounded-xl text-sm hover:bg-[#2C4C3B]"
                  >
                    Continue <ArrowRight size={14} weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="label-kicker anim-in delay-1">Conflict Coach for Executives</div>
          <h1
            data-testid="hero-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1E3A2B] mt-6 max-w-4xl anim-in delay-2"
          >
            Conflict Coach for Executives
          </h1>
          <p
            data-testid="hero-subheadline"
            className="mt-8 max-w-3xl text-lg sm:text-xl text-[#4A524C] leading-relaxed anim-in delay-3"
          >
            Real-Time Conflict Guidance for Leaders When the Stakes Are High
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl anim-in delay-4">
            {CHOICES.map((c) => {
              const Icon = c.icon;
              return (
                <Link
                  key={c.to}
                  to={c.to}
                  data-testid={c.testid}
                  className="group inline-flex items-center gap-3 bg-white border border-[#E8E4DB] rounded-2xl px-5 py-4 hover:border-[#1E3A2B] hover:shadow-sm transition-all"
                >
                  <span className="w-10 h-10 rounded-full bg-[#1E3A2B]/8 text-[#1E3A2B] flex items-center justify-center flex-shrink-0">
                    <Icon size={18} weight="duotone" />
                  </span>
                  <span className="flex-1 text-[#121815] font-medium leading-tight">{c.label}</span>
                </Link>
              );
            })}
          </div>
          <div className="mt-6 anim-in delay-5">
            <Link
              to="/walkthrough?mode=quick"
              data-testid="home-cta-ask-secondary"
              className="text-sm text-[#4A524C] hover:text-[#1E3A2B] underline decoration-[#1E3A2B]/30 underline-offset-4 inline-flex items-center gap-1"
            >
              <ChatCircleDots size={14} weight="duotone" /> Or ask a quick question →
            </Link>
          </div>
        </div>
      </section>

      {/* Foundation */}
      <section className="border-t border-[#E8E4DB] bg-[#F3F0EA]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-24 lg:py-28 text-center">
          <div className="label-kicker">Foundation</div>
          <h2 className="font-serif text-3xl lg:text-4xl text-[#1E3A2B] mt-4 tracking-tight leading-tight">
            Built for the realities of executive leadership.
          </h2>
          <p className="mt-6 text-[#4A524C] leading-relaxed">
            Anchored in proven collaborative-conflict research and tuned for
            the conditions executives actually face: power asymmetry, organizational
            stakes, and decisions that cannot wait.
          </p>
        </div>
      </section>

      {/* CGI tagline + standard disclaimer */}
      <section className="border-t border-[#E8E4DB] bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-14 lg:py-16 text-center">
          <p
            data-testid="home-cgi-tagline"
            className="text-[#4A524C] leading-relaxed"
          >
            Explore{" "}
            <a
              href="https://commongroundinstitute.net"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1E3A2B] underline decoration-[#1E3A2B]/30 underline-offset-4 hover:decoration-[#1E3A2B]"
            >
              CGI's
            </a>{" "}
            executive leadership coaching, leadership workshops and interactive
            courses for employee training.
          </p>
          <div className="mt-8 max-w-2xl mx-auto">
            <Disclaimer variant="compact" />
          </div>
        </div>
      </section>
    </div>
  );
}
